import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CreateCustomer } from '../kingfisher/create-customer';
import { Customer } from '../kingfisher/customer';
import {
  buildCiamCallbackUrl,
  CiamCustomerProfile,
  CiamTokenResponse,
  CiamTokens,
  mapCiamProfileToCustomer,
  toCiamCustomerUpdate,
} from './ciam.models';

export { CiamTokens } from './ciam.models';

const TOKENS_STORAGE_KEY = 'castorama.ciam.tokens';
const RETURN_URL_STORAGE_KEY = 'castorama.ciam.returnUrl';
const SESSION_CHANNEL_NAME = 'castorama.ciam.session';
const LOGOUT_MESSAGE = 'logout';

interface CiamConfig {
  authAppBaseUrl: string;
  bffBaseUrl: string;
}

/**
 * Parcours CIAM du site Customer.
 *
 * AuthApp collecte seul les identifiants ; toutes les opérations gateway (échange de la session,
 * refresh, logout, profil GCI) passent par le BFF Castorama2D. Aucune clé Tyk ni base GAuth/GCI
 * n'est donc embarquée dans le bundle.
 */
@Injectable({
  providedIn: 'root',
})
export class CiamService {
  /**
   * Les jetons vivent dans le sessionStorage de chaque onglet : supprimer la session CIAM ne les
   * efface pas ailleurs, et un autre onglet resterait connecté jusqu'à leur expiration. La
   * déconnexion est donc diffusée aux autres onglets du site, qui purgent leurs propres jetons.
   */
  private readonly sessionChannel: BroadcastChannel | null =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SESSION_CHANNEL_NAME);

  /** Émis quand le client s'est déconnecté dans un autre onglet ; les jetons de cet onglet sont déjà purgés. */
  readonly loggedOutElsewhere = new Subject<void>();

  constructor(private readonly http: HttpClient) {
    this.sessionChannel?.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === LOGOUT_MESSAGE) {
        this.clearSessionStorage();
        this.loggedOutElsewhere.next();
      }
    });
  }

  redirectToSignin(returnUrl: string = this.currentRelativeUrl()): void {
    this.redirectToAuthApp('/signin', returnUrl);
  }

  /**
   * AuthApp ne collecte que les credentials. Les attributs qu'il ignore (civilité, nom, prénom,
   * téléphone, adresse) ne traversent donc plus la redirection : ils sont collectés au retour
   * par la popup de complétion, une fois le client authentifié.
   */
  redirectToRegistration(returnUrl: string = this.currentRelativeUrl()): void {
    this.redirectToAuthApp('/register', returnUrl);
  }

  redirectToForgotPassword(returnUrl: string = this.currentRelativeUrl(), email?: string): void {
    this.redirectToAuthApp('/forgot-password', returnUrl, email ? { email } : {});
  }

  /**
   * Variante de `redirectToSignin` pour les écrans qui portent un travail non rejouable : une
   * redirection pleine page perdrait la configuration en cours. AuthApp s'ouvre donc dans un
   * onglet séparé, l'onglet appelant reste vivant.
   *
   * Le nouvel onglet revient sur l'accueil et non sur l'URL courante : dupliquer un configurateur
   * n'apporterait rien, seul compte le cookie de session posé par AuthApp, partagé entre onglets.
   *
   * Retourne `false` si le navigateur a bloqué l'ouverture. La détection impose de récupérer la
   * référence de l'onglet, donc de ne pas passer `noopener` qui ferait toujours retourner `null`.
   */
  openSigninInNewTab(): boolean {
    return !!window.open(this.buildAuthAppUrl('/signin', '/'), '_blank');
  }

  /**
   * Échange le cookie de session AuthApp contre une paire de jetons.
   * L'appel est credentialed : le BFF doit être servi sous un domaine couvert par le cookie.
   */
  async exchangeSessionForTokens(): Promise<CiamTokens> {
    const response = await firstValueFrom(
      this.http.get<CiamTokenResponse>(this.bffUrl('ciam/session/tokens'), {
        withCredentials: true,
      })
    );

    const tokens = this.normalizeTokens(response);
    this.persistTokens(tokens);
    return tokens;
  }

  /**
   * GAuth n'expose pas de rotation par refresh token pour le web : la seule façon de renouveler
   * une session est de ré-échanger le cookie AuthApp, qui reste la source de vérité. Le refresh
   * token reçu n'est donc pas utilisé côté navigateur.
   */
  async restoreOrRefreshTokens(): Promise<CiamTokens | null> {
    const tokens = this.restoreTokens();
    if (!tokens) {
      return null;
    }

    if (!this.shouldRefresh(tokens)) {
      return tokens;
    }

    try {
      return await this.exchangeSessionForTokens();
    } catch (err) {
      this.clearTokens();

      // Plus de cookie exploitable : le client n'est plus connecté, ce n'est pas une panne.
      if (this.isMissingSession(err)) {
        return null;
      }

      throw err;
    }
  }

  async getCurrentCustomer(accessToken: string): Promise<Customer> {
    const profile = await firstValueFrom(
      this.http.get<CiamCustomerProfile>(this.bffUrl('ciam/customer'), {
        headers: this.bearerHeaders(accessToken),
      })
    );

    return mapCiamProfileToCustomer(profile);
  }

  async updateCurrentCustomer(accessToken: string, customer: CreateCustomer): Promise<Customer> {
    const profile = await firstValueFrom(
      this.http.patch<CiamCustomerProfile>(this.bffUrl('ciam/customer'), toCiamCustomerUpdate(customer), {
        headers: this.bearerHeaders(accessToken),
      })
    );

    return mapCiamProfileToCustomer(profile);
  }

  /**
   * La déconnexion est partagée entre CIAM et le consumer : le BFF supprime la session CIAM,
   * mais les jetons et l'URL de retour que le site conserve doivent l'être ici, dans cet onglet
   * comme dans les autres. Ils sont purgés avant l'appel, qui peut échouer sans les laisser derrière.
   */
  async logout(): Promise<void> {
    this.clearSessionStorage();
    this.sessionChannel?.postMessage({ type: LOGOUT_MESSAGE });

    await firstValueFrom(
      this.http.delete<void>(this.bffUrl('ciam/session'), {
        withCredentials: true,
      })
    );
  }

  persistTokens(tokens: CiamTokens): void {
    sessionStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  }

  restoreTokens(): CiamTokens | null {
    const stored = sessionStorage.getItem(TOKENS_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    return JSON.parse(stored) as CiamTokens;
  }

  clearTokens(): void {
    sessionStorage.removeItem(TOKENS_STORAGE_KEY);
  }

  private clearSessionStorage(): void {
    this.clearTokens();
    sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
  }

  /**
   * URL mémorisée au départ vers AuthApp, lue une seule fois au retour. Elle double le paramètre
   * `returnUrl` du callback, que rien ne garantit qu'AuthApp conserve dans `redirectTo` — un
   * passage par l'inscription, notamment, peut ramener sur le callback nu.
   */
  consumeReturnUrl(): string | null {
    const returnUrl = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
    sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
    return returnUrl;
  }

  private redirectToAuthApp(path: string, returnUrl: string, extraParams: Record<string, string> = {}): void {
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, returnUrl || '/');
    window.location.assign(this.buildAuthAppUrl(path, returnUrl, extraParams));
  }

  private buildAuthAppUrl(path: string, returnUrl: string, extraParams: Record<string, string> = {}): string {
    const authUrl = new URL(path, this.withTrailingSlash(this.config.authAppBaseUrl));
    authUrl.searchParams.set('redirectTo', buildCiamCallbackUrl(window.location.origin, returnUrl));
    Object.entries(extraParams).forEach(([key, value]) => authUrl.searchParams.set(key, value));

    return authUrl.toString();
  }

  private normalizeTokens(response: CiamTokenResponse | null): CiamTokens {
    const accessToken = response?.accessToken;

    if (!accessToken) {
      throw new Error("La reponse CIAM ne contient pas d'access token.");
    }

    return {
      accessToken,
      refreshToken: response?.refreshToken,
      expiresAt: response?.expiresIn ? Date.now() + Number(response.expiresIn) * 1000 : undefined,
    };
  }

  private isMissingSession(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
  }

  private shouldRefresh(tokens: CiamTokens): boolean {
    const refreshSkewMs = 5 * 60 * 1000;
    return !!tokens.expiresAt && Date.now() >= tokens.expiresAt - refreshSkewMs;
  }

  private bearerHeaders(accessToken: string): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${accessToken}` });
  }

  private currentRelativeUrl(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
  }

  private withTrailingSlash(baseUrl: string): string {
    return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  }

  private bffUrl(path: string): string {
    return `${this.config.bffBaseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  private requireConfigValue(value: unknown, key: string): string {
    if (typeof value !== 'string' || !value.trim() || value.includes('#{')) {
      throw new Error(`Configuration CIAM manquante : ${key}.`);
    }

    return value;
  }

  private get config(): CiamConfig {
    const ciam = ((environment.castorama as any)?.ciam ?? {}) as Record<string, string | undefined>;

    return {
      authAppBaseUrl: this.requireConfigValue(ciam.authAppBaseUrl, 'castorama.ciam.authAppBaseUrl'),
      bffBaseUrl: this.requireConfigValue((environment as any).configurators2d?.baseApi, 'configurators2d.baseApi'),
    };
  }
}
